import api from './base'
import { endpoints } from './base'
import { OdkForm, OdkFormStateUpdatePayload } from '../../pages/projects/forms/types'

/**
 * Получение списка форм для проекта
 */
export const getOrganizationProjectForms = async (organizationId: number, projectId: number): Promise<OdkForm[]> => {
  try {
    const url = endpoints.organizationProjectForms.list(organizationId, projectId)
    // console.log('API Request getOrganizationProjectForms:', url)
    const response = await api.get(url)
    // console.log('API Response getOrganizationProjectForms:', response.data, 'Type:', typeof response.data)

    // Если API возвращает пустую строку или null, возвращаем пустой массив
    if (response.data === '' || response.data === null || response.data === undefined) {
      return []
    }

    // Если это массив, возвращаем как есть
    if (Array.isArray(response.data)) {
      return response.data
    }

    // Если это не массив и не пустое значение, логируем предупреждение и возвращаем пустой массив
    console.warn('API returned non-array data:', response.data)
    return []
  } catch (error: any) {
    console.error('API Error getOrganizationProjectForms:', error.response || error)
    throw error
  }
}

/**
 * Получение детальной информации о форме
 */
export const getOrganizationProjectFormDetails = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
): Promise<OdkForm> => {
  try {
    const response = await api.get(endpoints.organizationProjectForms.details(organizationId, projectId, xmlFormId))
    return response.data
  } catch (error: any) {
    console.error('API Error getOrganizationProjectFormDetails:', error.response || error)
    throw error
  }
}

/**
 * Получение полей формы
 */
export const getOrganizationProjectFormFields = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
) => {
  try {
    const response = await api.get(endpoints.organizationProjectForms.fields(organizationId, projectId, xmlFormId))
    return response.data
  } catch (error: any) {
    console.error('API Error getOrganizationProjectFormFields:', error.response || error)
    throw error
  }
}

/**
 * Получение списка вложений формы
 */
export const getOrganizationProjectFormAttachments = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
) => {
  try {
    const response = await api.get(
      endpoints.organizationProjectForms.listAttachments(organizationId, projectId, xmlFormId),
    )
    return response.data
  } catch (error: any) {
    console.error('API Error getOrganizationProjectFormAttachments:', error.response || error)
    throw error
  }
}

/**
 * Скачивание вложения формы
 */
export const downloadOrganizationProjectFormAttachment = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
  filename: string,
): Promise<Blob> => {
  try {
    const response = await api.get(
      endpoints.organizationProjectForms.downloadAttachment(organizationId, projectId, xmlFormId, filename),
      { responseType: 'blob' },
    )
    return response.data
  } catch (error: any) {
    console.error('API Error downloadOrganizationProjectFormAttachment:', error.response || error)
    throw error
  }
}

/**
 * Загрузка вложения формы
 */
export const uploadOrganizationProjectFormAttachment = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
  filename: string,
  file: File,
): Promise<any> => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post(
      endpoints.organizationProjectForms.uploadAttachment(organizationId, projectId, xmlFormId, filename),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    return response.data
  } catch (error: any) {
    console.error('API Error uploadOrganizationProjectFormAttachment:', error.response || error)
    throw error
  }
}

export const deleteOrganizationProjectFormAttachment = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
  filename: string,
): Promise<any> => {
  try {
    const response = await api.delete(
      endpoints.organizationProjectForms.deleteAttachment(organizationId, projectId, xmlFormId, filename),
    )

    return response.data
  } catch (error: any) {
    console.error('API Error deleteOrganizationProjectFormAttachment:', error.response || error)
    throw error
  }
}

/**
 * Обновление состояния формы (статуса, доступности и т.д.)
 */
export const updateOrganizationProjectFormState = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
  payload: OdkFormStateUpdatePayload,
): Promise<OdkForm> => {
  try {
    const response = await api.patch(
      endpoints.organizationProjectForms.updateState(organizationId, projectId, xmlFormId),
      payload,
    )
    return response.data
  } catch (error: any) {
    console.error('API Error updateOrganizationProjectFormState:', error.response || error)
    throw error
  }
}

/**
 * Удаление формы
 */
export const deleteOrganizationProjectForm = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
): Promise<{ message: string; deleted_from_odk: boolean }> => {
  try {
    const response = await api.delete(endpoints.organizationProjectForms.delete(organizationId, projectId, xmlFormId))
    return response.data
  } catch (error: any) {
    console.error('API Error deleteOrganizationProjectForm:', error.response || error)
    throw error
  }
}

/**
 * Синхронизация форм проекта
 */
export const syncOrganizationProjectForms = async (organizationId: number, projectId: number) => {
  try {
    const response = await api.post(endpoints.organizationProjectForms.syncAll(organizationId, projectId))
    return response.data
  } catch (error: any) {
    console.error('API Error syncOrganizationProjectForms:', error.response || error)
    throw error
  }
}

// --- Publish Draft Form Function ---
export const publishOdkDraftForm = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
  version?: string, // Optional version parameter
): Promise<OdkForm> => {
  // Get the correct URL without query params in the template
  const url = endpoints.organizationProjectForms.publishDraft(projectId, xmlFormId)

  // Prepare data for the request body
  const requestBody: { version?: string } = {}
  if (version) {
    requestBody.version = version
  }

  // Prepare query parameters
  const queryParams = {
    organization_id: organizationId, // Pass organizationId as query param
  }

  try {
    // Make the POST request with body and query parameters
    const response = await api.post<OdkForm>(url, requestBody, { params: queryParams })
    return response.data
  } catch (error: any) {
    console.error(`[API Service] Error publishing draft form ${xmlFormId}:`, error.response?.data || error.message)
    throw error // Re-throw the error to be handled by the caller (store)
  }
}
// --- End Publish Draft Form Function ---

/**
 * Get submission count for a form (lightweight alternative to full analytics).
 */
export const getFormSubmissionsCount = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
): Promise<number> => {
  try {
    const response = await api.get(
      `/org/${organizationId}/odk-projects/${projectId}/forms/${encodeURIComponent(xmlFormId)}/submissions/count`,
    )
    return Number(response.data?.count ?? 0)
  } catch (error: any) {
    console.error('API Error getFormSubmissionsCount:', error.response || error)
    throw error
  }
}

// --- New Form Analytics Function ---
/**
 * Получение аналитики формы, включая статистику отправок, геоданные и др.
 */
export const getOrganizationProjectFormAnalytics = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
): Promise<any> => {
  try {
    const url = endpoints.organizationProjectForms.analytics(organizationId, projectId, xmlFormId)
    const response = await api.get(url)
    return response.data
  } catch (error: any) {
    console.error('API Error getOrganizationProjectFormAnalytics:', error.response || error)
    throw error
  }
}
// --- End Form Analytics Function ---

export interface OdkFormVersion {
  version: string
  publishedAt?: string | null
  sha?: string | null
  publishedBy?: string | { displayName?: string; [key: string]: unknown } | null
}

/**
 * List published form versions from ODK Central.
 */
export const getOrganizationProjectFormVersions = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
  extendedMetadata: boolean = true,
): Promise<OdkFormVersion[]> => {
  try {
    const response = await api.get(endpoints.organizationProjectForms.versions(organizationId, projectId, xmlFormId), {
      params: { extended_metadata: extendedMetadata },
    })
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('API Error getOrganizationProjectFormVersions:', error.response || error)
    throw error
  }
}

/**
 * Download XLSX for a specific published form version.
 */
export const downloadOrganizationProjectFormVersionXlsx = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
  version: string,
): Promise<Blob> => {
  try {
    const response = await api.get(
      endpoints.organizationProjectForms.versionXlsx(organizationId, projectId, xmlFormId, version),
      { responseType: 'blob' },
    )
    return response.data
  } catch (error: any) {
    console.error('API Error downloadOrganizationProjectFormVersionXlsx:', error.response || error)
    throw error
  }
}

export interface OdkFormPublicLink {
  id: number
  displayName?: string
  token?: string | null
  once?: boolean
  url?: string | null
  enketoId?: string | null
  createdAt?: string
  deletedAt?: string | null
}

/**
 * List Public Access Links for a form (includes Enketo URL with ?st= token).
 */
export const getOrganizationProjectFormPublicLinks = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
): Promise<OdkFormPublicLink[]> => {
  try {
    const response = await api.get(endpoints.organizationProjectForms.publicLinks(organizationId, projectId, xmlFormId))
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('API Error getOrganizationProjectFormPublicLinks:', error.response || error)
    throw error
  }
}

/**
 * Download current published (or draft) XLSForm as blob.
 */
export const downloadOrganizationProjectFormXlsx = async (
  organizationId: number,
  projectId: number,
  xmlFormId: string,
  isDraft: boolean = false,
): Promise<Blob> => {
  try {
    const url = isDraft
      ? `/odk-forms/${projectId}/${encodeURIComponent(xmlFormId)}/draft.xlsx`
      : endpoints.organizationProjectForms.xlsx(organizationId, projectId, xmlFormId)
    const response = await api.get(url, {
      params: isDraft ? { organization_id: organizationId } : undefined,
      responseType: 'blob',
    })
    return response.data
  } catch (error: any) {
    console.error('API Error downloadOrganizationProjectFormXlsx:', error.response || error)
    throw error
  }
}

// --- XLSForm Upload Function ---
/**
 * Загрузка XLSForm файла в проект
 */
export const uploadXlsform = async (
  organizationId: number,
  projectId: number,
  file: File,
  formId: string,
  formUpdate: boolean = false,
  ignoreWarnings: boolean = true,
  publish: boolean = false,
): Promise<any> => {
  try {
    // Создаем FormData для multipart/form-data запроса
    const formData = new FormData()
    formData.append('file', file)
    formData.append('form_id', formId)
    formData.append('form_update', formUpdate.toString())
    formData.append('ignore_warnings', ignoreWarnings.toString())
    formData.append('publish', publish.toString())

    const url = endpoints.organizationProjectForms.uploadXlsform(organizationId, projectId)

    const response = await api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return response.data
  } catch (error: any) {
    console.error(`[API Service] Error uploading XLSForm ${formId}:`, error.response?.data || error.message)
    throw error
  }
}
// --- End XLSForm Upload Function ---
